/*
 * VITER – buy form for the final CTA and the sticky add-to-cart bar.
 *
 * <viter-buy-form>
 *   - Wraps a real `{% form 'product' %}` (works without JS).
 *   - On submit delegates to the main Dawn product form of the same product (so the selected variant,
 *     selling plan and app line-item properties are used, and Dawn opens the cart drawer/notification).
 *     Without a main form on the page, Dawn <product-form> inside handles the AJAX add itself.
 *   - Keeps `name="id"`, label, price text and disabled state in sync with PUB_SUB_EVENTS.variantChange.
 *     Prices are never shown inside the button (PUFO rule). Price text = `[data-viter-price]` inside the form or
 *     inside the closest `[data-viter-buy-scope]` wrapper (e.g. the sticky bar info).
 *
 * <viter-sticky-atc>
 *   - Shows the bar once the main add-to-cart button has scrolled out of view (above the viewport)
 *     and no final CTA is visible; hides while a drawer/modal locks the page scroll.
 *   - `inert` + `aria-hidden` while hidden; reserves bottom padding on <body> while shown.
 */

(() => {
  const findMainProductInfo = (productId) => {
    if (!productId) return null;
    const candidates = document.querySelectorAll(`product-info[data-product-id="${productId}"]`);
    for (const info of candidates) {
      if (info.closest('quick-add-modal, viter-buy-form, viter-sticky-atc')) continue;
      if (info.querySelector('form[action*="/cart/add"]')) return info;
    }
    return null;
  };

  const findMainForm = (info) => {
    if (!info) return null;
    return (
      info.querySelector('product-form form[action*="/cart/add"]') ||
      info.querySelector('form[action*="/cart/add"]')
    );
  };

  const hasPubSub = () => typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';

  if (!customElements.get('viter-buy-form')) {
    customElements.define(
      'viter-buy-form',
      class ViterBuyForm extends HTMLElement {
        constructor() {
          super();
          this.onSubmit = this.onSubmit.bind(this);
          this.unsubscribers = [];
          this.pending = false;
          this.pendingTimer = null;
        }

        connectedCallback() {
          this.form = this.querySelector('form[action*="/cart/add"]');
          this.button = this.querySelector('button[name="add"]');
          this.idInput = this.form?.querySelector('input[name="id"]');
          this.labelEl = this.querySelector('[data-viter-label]');
          const scope = this.closest('[data-viter-buy-scope]') || this;
          this.priceEls = scope.querySelectorAll('[data-viter-price]');
          this.chooseLink = this.querySelector('[data-viter-choose]');
          this.errorWrapper = this.querySelector('.viter-buy-form__error');
          this.showPrice = this.dataset.showPrice === 'true';
          this.productAvailable = this.dataset.productAvailable === 'true';
          this.variants = this.readVariants();

          // Capture phase: runs before Dawn <product-form> listens on the <form> itself.
          this.addEventListener('submit', this.onSubmit, true);

          if (hasPubSub()) {
            this.unsubscribers.push(
              subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event)),
              subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.setPending(false)),
              subscribe(PUB_SUB_EVENTS.cartError, (event) => this.onCartError(event))
            );
          }

          this.syncFromMainForm();
        }

        disconnectedCallback() {
          this.removeEventListener('submit', this.onSubmit, true);
          this.unsubscribers.forEach((unsubscribe) => unsubscribe());
          this.unsubscribers = [];
          clearTimeout(this.pendingTimer);
        }

        readVariants() {
          const script = this.querySelector('script[data-viter-variants]');
          if (!script) return {};
          try {
            return JSON.parse(script.textContent);
          } catch (error) {
            return {};
          }
        }

        get mainProductInfo() {
          return findMainProductInfo(this.dataset.productId);
        }

        onSubmit(event) {
          const mainForm = findMainForm(this.mainProductInfo);
          // No main form on the page → let Dawn <product-form> (or the native POST) handle it.
          if (!mainForm || mainForm === event.target) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          if (this.button?.disabled || this.pending) return;

          const mainButton = mainForm.querySelector('[type="submit"][name="add"]');
          if (!mainButton || mainButton.disabled) {
            this.scrollToMainProduct();
            return;
          }
          // Main form is busy (request in flight).
          if (mainButton.getAttribute('aria-disabled') === 'true') return;

          this.hideError();
          this.setPending(true);

          if (typeof mainForm.requestSubmit === 'function') {
            mainForm.requestSubmit(mainButton);
          } else {
            mainButton.click();
          }
        }

        onCartError(event) {
          if (!this.pending) return;
          this.setPending(false);
          const message = event?.message || event?.errors;
          if (typeof message === 'string' && message) this.showError(message);
        }

        setPending(isPending) {
          this.pending = isPending;
          clearTimeout(this.pendingTimer);
          if (!this.button) return;

          this.button.classList.toggle('loading', isPending);
          this.button.toggleAttribute('aria-busy', isPending);
          this.querySelector('.loading__spinner')?.classList.toggle('hidden', !isPending);

          // Safety net (e.g. Dawn redirects to /cart when cart type is "page").
          if (isPending) this.pendingTimer = setTimeout(() => this.setPending(false), 10000);
        }

        showError(message) {
          if (!this.errorWrapper) return;
          const text = this.errorWrapper.querySelector('.product-form__error-message');
          if (text) text.textContent = message;
          this.errorWrapper.hidden = false;
        }

        hideError() {
          if (this.errorWrapper) this.errorWrapper.hidden = true;
        }

        scrollToMainProduct() {
          const info = this.mainProductInfo;
          if (!info) return;
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          info.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
          const firstInput = info.querySelector('variant-selects input:not([disabled]), variant-selects select');
          firstInput?.focus({ preventScroll: true });
        }

        syncFromMainForm() {
          const mainForm = findMainForm(this.mainProductInfo);
          const mainId = mainForm?.querySelector('input[name="id"]')?.value;
          if (mainForm && mainId !== undefined && mainId !== this.idInput?.value) {
            this.updateVariant(mainId || null);
          } else {
            this.updateChooseLink(this.isCurrentAvailable());
          }
        }

        onVariantChange(event) {
          const data = event?.data;
          if (!data) return;
          const info = this.mainProductInfo;
          if (!info) return;
          const infoSectionId = info.dataset.originalSection || info.dataset.section;
          if (data.sectionId !== infoSectionId) return;

          const variant = data.variant;
          this.updateVariant(variant ? String(variant.id) : null, variant);
        }

        isCurrentAvailable() {
          return Boolean(this.button && !this.button.disabled);
        }

        updateVariant(variantId, variantObject) {
          const entry = variantId ? this.variants[variantId] : null;
          const available = entry ? entry.available : Boolean(variantObject?.available);

          if (this.idInput) {
            this.idInput.value = variantId || '';
            this.idInput.disabled = !variantId;
          }

          if (this.button) {
            this.button.disabled = !available;
            this.button.removeAttribute('aria-disabled');
          }

          if (this.labelEl) {
            if (!variantId) this.labelEl.textContent = this.dataset.labelUnavailable;
            else this.labelEl.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldOut;
          }

          const price = entry?.price;
          this.priceEls.forEach((el) => {
            if (price) el.textContent = price;
            el.hidden = !(variantId && price);
          });

          // Reset a sold-out message left by Dawn <product-form> after an error.
          this.querySelector('.sold-out-message')?.classList.add('hidden');
          this.querySelector('.viter-buy-form__text')?.classList.remove('hidden');
          this.hideError();

          this.updateChooseLink(available);
        }

        updateChooseLink(available) {
          if (!this.chooseLink) return;
          const info = this.mainProductInfo;
          const show = !available && this.productAvailable && Boolean(info);
          if (show && info.id) this.chooseLink.setAttribute('href', `#${info.id}`);
          this.chooseLink.hidden = !show;
          if (show && !this.chooseLink.dataset.bound) {
            this.chooseLink.dataset.bound = 'true';
            this.chooseLink.addEventListener('click', (event) => {
              if (!this.mainProductInfo) return;
              event.preventDefault();
              this.scrollToMainProduct();
            });
          }
        }
      }
    );
  }

  if (!customElements.get('viter-sticky-atc')) {
    customElements.define(
      'viter-sticky-atc',
      class ViterStickyAtc extends HTMLElement {
        constructor() {
          super();
          this.entries = new Map();
          this.forceVisible = false;
          this.isShown = false;
          this.update = this.update.bind(this);
          this.onEditorChange = this.onEditorChange.bind(this);
          this.onSectionSelect = this.onSectionSelect.bind(this);
          this.onSectionDeselect = this.onSectionDeselect.bind(this);
          this.onScroll = this.onScroll.bind(this);
          this.onPageChange = this.onPageChange.bind(this);
        }

        onPageChange() {
          // Anchor jumps (#faq, #specyfikacja), back/forward navigation and late layout shifts after `load`
          // can move the page without a fresh IntersectionObserver callback – re-check after layout settles.
          window.requestAnimationFrame(() => {
            this.update();
            window.requestAnimationFrame(() => this.update());
          });
        }

        isTargetVisible(el) {
          // Real overlap with the viewport, not just a touching edge (IntersectionObserver reports
          // isIntersecting for a 0px overlap at the viewport boundary, e.g. right after an anchor jump).
          if (!el.isConnected) return false;
          const rect = el.getBoundingClientRect();
          if (rect.width === 0 && rect.height === 0) return false;
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          const minOverlap = Math.min(24, rect.height / 2);
          const overlap = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
          return overlap >= minOverlap;
        }

        onScroll() {
          // IntersectionObserver does not fire when the page jumps past the main button without it ever
          // entering the viewport (anchor links, restored scroll position) – re-check on scroll.
          if (this.scrollFrame) return;
          this.scrollFrame = window.requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.update();
          });
        }

        connectedCallback() {
          this.bar = this.querySelector('.viter-sticky-atc__bar') || this;
          this.setVisible(false);

          if (!('IntersectionObserver' in window)) return;

          this.observeTargets();

          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('load', this.onPageChange);
          window.addEventListener('hashchange', this.onPageChange);
          window.addEventListener('popstate', this.onPageChange);
          window.addEventListener('pageshow', this.onPageChange);
          window.addEventListener('resize', this.onScroll, { passive: true });

          this.bodyObserver = new MutationObserver(this.update);
          this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.updateBodyOffset());
            this.resizeObserver.observe(this.bar);
          }

          this.mediaQuery = window.matchMedia('(min-width: 750px)');
          this.mediaQuery.addEventListener?.('change', this.update);

          document.addEventListener('shopify:section:load', this.onEditorChange);
          document.addEventListener('shopify:section:unload', this.onEditorChange);
          document.addEventListener('shopify:section:reorder', this.onEditorChange);
          document.addEventListener('shopify:section:select', this.onSectionSelect);
          document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        }

        disconnectedCallback() {
          this.observer?.disconnect();
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('load', this.onPageChange);
          window.removeEventListener('hashchange', this.onPageChange);
          window.removeEventListener('popstate', this.onPageChange);
          window.removeEventListener('pageshow', this.onPageChange);
          window.removeEventListener('resize', this.onScroll);
          this.bodyObserver?.disconnect();
          this.resizeObserver?.disconnect();
          this.mediaQuery?.removeEventListener?.('change', this.update);
          document.removeEventListener('shopify:section:load', this.onEditorChange);
          document.removeEventListener('shopify:section:unload', this.onEditorChange);
          document.removeEventListener('shopify:section:reorder', this.onEditorChange);
          document.removeEventListener('shopify:section:select', this.onSectionSelect);
          document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
          document.body.classList.remove('viter-sticky-atc-active');
        }

        onEditorChange() {
          // Wait for the editor to swap the section DOM, then re-observe the new nodes.
          window.requestAnimationFrame(() => this.observeTargets());
        }

        onSectionSelect(event) {
          if (event.detail?.sectionId !== this.dataset.sectionId) return;
          this.forceVisible = true;
          this.update();
        }

        onSectionDeselect(event) {
          if (event.detail?.sectionId !== this.dataset.sectionId) return;
          this.forceVisible = false;
          this.update();
        }

        observeTargets() {
          this.observer?.disconnect();
          this.entries.clear();

          const info = findMainProductInfo(this.dataset.productId);
          const mainForm = findMainForm(info);
          this.mainButton = mainForm?.querySelector('[type="submit"][name="add"]') || null;
          this.ctaTargets = Array.from(document.querySelectorAll('[data-viter-sticky-hide]')).filter(
            (el) => !this.contains(el)
          );

          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.entries.set(entry.target, {
                  isIntersecting: entry.isIntersecting && entry.intersectionRatio > 0,
                  isAbove: entry.boundingClientRect.bottom <= 0,
                });
              });
              this.update();
            },
            { threshold: [0, 0.01, 0.25, 0.5, 1] }
          );

          if (this.mainButton) this.observer.observe(this.mainButton);
          this.ctaTargets.forEach((el) => this.observer.observe(el));
          this.update();
          this.onPageChange();
        }

        isEnabledAtViewport() {
          return window.getComputedStyle(this).display !== 'none';
        }

        update() {
          let show;

          if (this.mainButton) {
            show = this.mainButton.isConnected && this.mainButton.getBoundingClientRect().bottom <= 0;
          } else {
            // No main product form on the page: the bar is the only quick way to buy.
            show = true;
          }

          if (this.ctaTargets?.some((el) => this.isTargetVisible(el))) show = false;

          const bodyClasses = document.body.className;
          if (/\boverflow-hidden(-\w+)?\b/.test(bodyClasses)) show = false;

          if (this.forceVisible) show = true;

          this.setVisible(show && this.isEnabledAtViewport());
        }

        setVisible(visible) {
          if (visible === this.isShown && this.hasAttribute('data-initialized')) return;
          this.setAttribute('data-initialized', '');
          this.isShown = visible;

          this.toggleAttribute('data-visible', visible);
          this.bar.inert = !visible;
          this.bar.setAttribute('aria-hidden', String(!visible));

          if (!visible && this.bar.contains(document.activeElement)) document.activeElement.blur();

          document.body.classList.toggle('viter-sticky-atc-active', visible);
          this.updateBodyOffset();
        }

        updateBodyOffset() {
          const height = this.isShown ? this.bar.getBoundingClientRect().height : 0;
          document.body.style.setProperty('--viter-sticky-atc-height', `${Math.ceil(height)}px`);
        }
      }
    );
  }
})();
